import React, { useEffect, useRef, useCallback, useState } from 'react';
import { usePlayerStore } from './playerStore';
import { nativeAudioService } from '../../services/nativeAudioService';
import { Capacitor } from '@capacitor/core';
import { preloadAudioWithRetry, handleAudioError, getPlatform, shouldUseNativeAudio } from '../../utils/audioHelper';
import { PlaybackTimingController } from './PlaybackTimingController';

interface HybridAudioSyncProps {
  audioUrl: string | null;
  onError?: (error: Error) => void;
}

/**
 * HybridAudioSync - Enhanced audio component with automatic fallback
 * 1. Tries native audio first on mobile platforms
 * 2. Falls back to HTML5 audio if native fails
 * 3. Provides consistent API regardless of implementation
 */
export const HybridAudioSync: React.FC<HybridAudioSyncProps> = ({ audioUrl, onError }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const assetIdRef = useRef<string>('recording-audio');
  const [audioMode, setAudioMode] = useState<'native' | 'html5' | 'detecting'>('detecting');
  const [nativeError, setNativeError] = useState<Error | null>(null);
  const [audioBuffered, setAudioBuffered] = useState(false);
  const lastTimeUpdateRef = useRef<number>(0);
  const platform = getPlatform();
  const timingController = useRef(PlaybackTimingController.getInstance());
  
  const { 
    setAudioElement, 
    updateCurrentTime,
    isPlaying,
    playbackSpeed,
    currentTime,
    duration,
  } = usePlayerStore();
  
  const isSeeking = usePlayerStore(state => state.isSeeking);

  // Initialize and determine audio mode
  useEffect(() => {
    const initializeAudio = async () => {
      try {
        // First, try to initialize native audio service
        await nativeAudioService.initialize();
        
        if (shouldUseNativeAudio() && nativeAudioService.isNative()) {
          setAudioMode('native');
          // Clear any existing audio errors
          usePlayerStore.getState().setHasAudioError(false);
        } else {
          setAudioMode('html5');
          
          // Set audio element for HTML5 mode
          if (audioRef.current) {
            setAudioElement(audioRef.current);
            // Clear any existing audio errors
            usePlayerStore.getState().setHasAudioError(false);
          }
        }
      } catch (error) {
        setAudioMode('html5');
        setNativeError(error as Error);
        
        if (audioRef.current) {
          setAudioElement(audioRef.current);
        }
      }
    };

    initializeAudio();
  }, [setAudioElement, platform]);

  // Preload audio based on mode
  useEffect(() => {
    if (!audioUrl || audioMode === 'detecting') return;

    const preloadAudio = async () => {
      try {
        // Unload previous audio if exists
        if (audioMode === 'native' && nativeAudioService.isPreloaded(assetIdRef.current)) {
          await nativeAudioService.unload(assetIdRef.current);
        }

        if (audioMode === 'native') {
          try {
            // Try native audio with retry
            await preloadAudioWithRetry(async () => {
              await nativeAudioService.preloadAudio(assetIdRef.current, audioUrl, true);
            }, 2, 1000); // Reduced retries for faster fallback
            
            usePlayerStore.setState({ isAudioReady: true });
            
            // Log audio URL for debugging
          } catch (nativeError) {
            
            // Switch to HTML5 mode
            setAudioMode('html5');
            setNativeError(nativeError as Error);
            
            // Initialize HTML5 audio
            if (audioRef.current) {
              setAudioElement(audioRef.current);
              audioRef.current.src = audioUrl;
              audioRef.current.load();
            }
          }
        } else {
          // HTML5 audio mode with improved preloading
          if (audioRef.current) {
            audioRef.current.src = audioUrl;
            audioRef.current.preload = 'auto';
            
            // Enhanced audio loading with events
            const handleCanPlayThrough = () => {
              setAudioBuffered(true);
              usePlayerStore.setState({ isAudioReady: true });
              // Clear any previous audio errors since audio is now ready
              usePlayerStore.getState().setHasAudioError(false);
              console.log('Audio fully buffered and ready');
            };
            
            const handleLoadedMetadata = () => {
              if (audioRef.current) {
                const duration = audioRef.current.duration * 1000;
                usePlayerStore.setState({ duration });
              }
            };
            
            audioRef.current.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
            audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
            
            audioRef.current.load();
          }
        }
      } catch (error) {
        const errorMessage = handleAudioError(error, platform);
        onError?.(new Error(errorMessage));
      }
    };

    preloadAudio();

    return () => {
      // Cleanup
      if (audioMode === 'native' && nativeAudioService.isPreloaded(assetIdRef.current)) {
        nativeAudioService.unload(assetIdRef.current).catch(() => {});
      }
    };
  }, [audioUrl, audioMode, onError, setAudioElement, platform]);

  // Handle play/pause
  useEffect(() => {
    if (!audioUrl || audioMode === 'detecting') return;

    const handlePlayPause = async () => {
      try {
        if (audioMode === 'native') {
          if (isPlaying) {
            if (!nativeAudioService.isPreloaded(assetIdRef.current)) {
              return;
            }
            await nativeAudioService.play(assetIdRef.current);
            // Clear error state if play succeeds
            usePlayerStore.getState().setHasAudioError(false);
          } else {
            await nativeAudioService.pause(assetIdRef.current);
          }
        } else {
          // HTML5 audio
          if (audioRef.current) {
            if (isPlaying && audioRef.current.paused) {
              const playPromise = audioRef.current.play();
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  // Clear error state if play succeeds
                  usePlayerStore.getState().setHasAudioError(false);
                }).catch((error) => {
                  if (error.name === 'NotAllowedError') {
                    // Wait for user interaction
                    const handleInteraction = () => {
                      if (usePlayerStore.getState().isPlaying && audioRef.current?.paused) {
                        audioRef.current.play().catch(console.error);
                      }
                    };
                    document.addEventListener('click', handleInteraction, { once: true });
                    document.addEventListener('touchstart', handleInteraction, { once: true });
                  } else {
                    // Only set error for actual audio issues
                    usePlayerStore.getState().setHasAudioError(true);
                  }
                });
              }
            } else if (!isPlaying && !audioRef.current.paused) {
              audioRef.current.pause();
            }
          }
        }
      } catch (error) {
        console.error('Failed to handle play/pause:', error);
        // Only set audio error for actual audio format/loading issues
        // Don't set error for play/pause issues if audio is working
        if (audioMode === 'html5' && audioRef.current && audioRef.current.error) {
          usePlayerStore.getState().setHasAudioError(true);
        }
      }
    };

    handlePlayPause();
  }, [isPlaying, audioMode]); // Removed audioUrl to prevent re-triggering on URL changes

  // Handle seeking (HTML5 only for now)
  useEffect(() => {
    if (!isSeeking || audioMode !== 'html5') return;

    const timeInSeconds = currentTime / 1000;
    if (audioRef.current && Math.abs(audioRef.current.currentTime - timeInSeconds) > 0.1) {
      audioRef.current.currentTime = timeInSeconds;
    }
  }, [currentTime, isSeeking, audioMode]);

  // Handle playback rate (HTML5 only)
  useEffect(() => {
    if (audioMode === 'html5' && audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, audioMode]);

  // Initialize timing controller synchronization
  useEffect(() => {
    if (isPlaying && audioRef.current && audioMode === 'html5') {
      // Start the sync loop with audio element
      timingController.current.startSyncLoop(audioRef.current);
      
      // Register callback to update store
      const unregister = timingController.current.registerSyncCallback((time) => {
        updateCurrentTime(time);
      });
      
      return () => {
        timingController.current.stopSyncLoop();
        unregister();
      };
    } else if (!isPlaying) {
      timingController.current.stopSyncLoop();
    }
  }, [isPlaying, audioMode, updateCurrentTime]);
  
  // Time update loop (simplified - timing controller handles sync)
  useEffect(() => {
    const updateTime = () => {
      if (audioMode === 'html5' && audioRef.current && !isSeeking && isPlaying) {
        const currentTimeMs = audioRef.current.currentTime * 1000;
        
        // Update timing controller with audio time
        timingController.current.updateAudioTime(currentTimeMs);
        
        // No need for throttling - timing controller handles it
        updateCurrentTime(currentTimeMs);
        
        // Check if ended
        if (audioRef.current.ended) {
          usePlayerStore.getState().pause();
          return;
        }
      } else if (audioMode === 'native' && isPlaying && !isSeeking) {
        // For native audio, use timer-based updates
        const elapsed = performance.now() - lastTimeUpdateRef.current;
        if (elapsed > 16) {
          const estimatedTime = currentTime + (elapsed * playbackSpeed);
          updateCurrentTime(estimatedTime);
          lastTimeUpdateRef.current = performance.now();
        }
      }

      animationFrameRef.current = requestAnimationFrame(updateTime);
    };

    if (isPlaying) {
      lastTimeUpdateRef.current = performance.now();
      updateTime();
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, isSeeking, updateCurrentTime, audioMode, currentTime, playbackSpeed]);

  // HTML5 audio event handlers
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      usePlayerStore.setState({ isAudioReady: true });
    }
  }, []);

  const handleError = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = e.currentTarget;
    console.error('HTML5 Audio error:', audio.error);
    const errorMessage = handleAudioError(audio.error || new Error('Unknown audio error'), platform);
    onError?.(new Error(errorMessage));
  }, [onError, platform]);

  const handleEnded = useCallback(() => {
    usePlayerStore.getState().pause();
  }, []);

  const handleCanPlayThrough = useCallback(() => {
    if (audioRef.current) {
      usePlayerStore.setState({ isAudioReady: true });
    }
  }, []);

  // Show native error warning if needed
  useEffect(() => {
    if (nativeError && audioMode === 'html5') {
    }
  }, [nativeError, audioMode]);
  
  // Cleanup timing controller on unmount
  useEffect(() => {
    return () => {
      timingController.current.reset();
    };
  }, []);

  // Always render audio element for fallback
  return (
    <>
      <audio
        ref={audioRef}
        preload="auto"
        className="hidden"
        onLoadedMetadata={handleLoadedMetadata}
        onError={handleError}
        onEnded={handleEnded}
        onCanPlayThrough={handleCanPlayThrough}
        style={{ display: 'none' }}
      />
      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ display: 'none' }}>
          Audio Mode: {audioMode} | Platform: {platform}
        </div>
      )}
    </>
  );
};